import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../../../apiClient";

/* -------------------------------------------------------------------------- */
/*                              helpers & catalog                              */
/* -------------------------------------------------------------------------- */

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id_${Math.random().toString(36).slice(2, 10)}`);

function latestSectionsByType(packet) {
  const out = Object.create(null);
  const arr = Array.isArray(packet?.sections) ? packet.sections.slice() : [];
  arr.sort((a, b) => (a?.order_index ?? 0) - (b?.order_index ?? 0));
  for (const s of arr) {
    const t = String(s?.type || "").toLowerCase();
    const curr = out[t];
    if (!curr || (s?.order_index ?? 0) >= (curr?.order_index ?? 0)) out[t] = s;
  }
  return out;
}

// Static catalog (no legacy aggregate "attachments" row)
const TYPE_CATALOG = [
  { type: "cover", label: "Cover Page" },
  { type: "income", label: "Basic Income" },
  { type: "income_advanced", label: "Advanced Income" },
  { type: "income_assessor", label: "Basic Income (Assessor)" },
  { type: "income_assessor_advanced", label: "Advanced Income (Assessor)" },
  { type: "income_actuals", label: "Basic Income (Actuals)" },
  { type: "income_actuals_advanced", label: "Advanced Income (Actuals)" },
  { type: "eu", label: "Equal & Uniform" },
  { type: "eu_chart", label: "E&U Bar Chart" },
  { type: "eu_income", label: "Equal & Uniform — Income" }, // NEW
  { type: "eu_income_chart", label: "E&U Income Bar Chart" },
  { type: "eu_map", label: "E&U Comp Map" },
  { type: "eu_income_map", label: "E&U Income Comp Map" },
  { type: "history", label: "History" },                     // NEW
  { type: "sales", label: "Sales Approach" },
  { type: "cost", label: "Cost Approach" },
  { type: "cost_assessor", label: "CAD Cost Approach (Beta)" },
];

function defaultOptionsForType(type) {
  const isCover = type === "cover";
  return {
    pageBreakBefore: !isCover,
    showNotes: false,
    showRawJson: false,
    orientation: (type === "eu" || type === "eu_income" || type === "eu_chart" || type === "eu_income_chart" || type === "eu_map" || type === "eu_income_map" || type === "history" || type === "cost" || type === "cost_assessor") ? "landscape" : "portrait", // NEW
    // CAD Cost Approach (Beta) — include the Methodology & Defense page by default.
    includeMethodology: type === "cost_assessor" ? true : undefined,
  };
}

// Normalize saved items, but DROP any legacy "attachments" aggregate row
function normalizeItems(items) {
  return (items || [])
    .filter((it) => String(it?.type || "").toLowerCase() !== "attachments")
    .map((it) => {
      const t = (it?.type || "").toLowerCase();
      const d = defaultOptionsForType(t);
      return {
        id: it.id || uid(),
        type: t,
        label: it.label || t,
        include: !!it.include,
        options: { ...d, ...(it.options || {}) },
      };
    });
}

function packetTitle(packet) {
  const coverTitle =
    Array.isArray(packet?.sections)
      ? (packet.sections.find((s) => String(s.type || "") === "cover")?.data?.property_info?.site_name || "")
      : "";
  return packet?.title || coverTitle || "Evidence Packet";
}

function Chip({ children }) { return <span className="chip">{children}</span>; }

function renderChipForType(type) {
  if (String(type).startsWith("income")) return <Chip>Income</Chip>;
  if (type === "eu") return <Chip>E&amp;U</Chip>;
  if (type === "eu_chart") return <Chip>E&amp;U&nbsp;Chart</Chip>;
  if (type === "eu_income") return <Chip>E&amp;U&nbsp;Income</Chip>; // NEW
  if (type === "eu_income_chart") return <Chip>E&amp;U&nbsp;Inc&nbsp;Chart</Chip>;
  if (type === "eu_map") return <Chip>E&amp;U&nbsp;Map</Chip>;
  if (type === "eu_income_map") return <Chip>E&amp;U&nbsp;Inc&nbsp;Map</Chip>;
  if (type === "attachment") return <Chip>Attachment</Chip>;
  return null;
}

function Select({ label, value, onChange, options }) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange?.(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2">
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CheckboxRow({ label, checked, onChange, disabled }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} disabled={disabled} />
      {label}
    </label>
  );
}

/* ------------------------ attachments as individual rows ------------------------ */

function gatherAttachments(latest) {
  const sec = latest["attachments"];
  const arr = Array.isArray(sec?.data?.attachments)
    ? sec.data.attachments
    : [];

  return arr
    .filter((a) => !!a?.url)
    .map((a, i) => {
      const label = a?.title || a?.name || `Attachment ${i + 1}`;
      const mime = a?.mime || "";
      const url = a?.url || "";

      const isMap = /map/i.test(label || "");

      // image detection by MIME OR file extension
      const isImage =
        mime.startsWith("image/") ||
        /\.(png|jpg|jpeg|gif|webp)$/i.test(url || "");

      const baseOptions = defaultOptionsForType("attachment");

      return {
        id: `att_${a?.id || i}`,
        type: "attachment",
        label,
        include: !!a?.include,
        options: {
          ...baseOptions,

          // NEW RULE:
          //  Map OR Image → default to landscape
          orientation:
            isMap || isImage
              ? "landscape"
              : (baseOptions.orientation || "portrait"),

          sourceSectionId: sec?.id ?? sec?.section_id ?? null,
          attachmentKey: a?.id ?? i,
        },
      };
    });
}

function buildDefaultItems(latest) {
  const statics = TYPE_CATALOG.map(({ type, label }) => ({
    id: uid(),
    type,
    label,
    include: !!latest[type],
    options: defaultOptionsForType(type),
  }));
  const atts = gatherAttachments(latest);
  return [...statics, ...atts];
}

// ADD: merge new static types into existing plan (so "History" appears even with old plans)
function mergeStaticsIntoPlan(plan, latest) {
  const next = { ...plan, items: plan.items.slice() };
  const have = new Set(next.items.map(it => `sec:${it.type}`));
  for (const { type, label } of TYPE_CATALOG) {
    const key = `sec:${type}`;
    if (!have.has(key)) {
      next.items.push({
        id: uid(),
        type,
        label,
        include: !!latest[type],
        options: defaultOptionsForType(type),
      });
      have.add(key);
    }
  }
  return next;
}

// merge (add/remove dynamic attachment rows) without clobbering user changes
function mergeAttachmentsIntoPlan(plan, latest) {
  const cleaned = { ...plan, items: plan.items.filter((it) => it.type !== "attachments") };
  const next = { ...cleaned, items: cleaned.items.slice() };
  const wanted = gatherAttachments(latest);

  const key = (it) =>
    it.type === "attachment"
      ? `att:${it.options.sourceSectionId}:${it.options.attachmentKey}`
      : `sec:${it.type}`;

  const existingKeys = new Set(next.items.map(key));
  for (const w of wanted) {
    const k = key(w);
    if (!existingKeys.has(k)) next.items.push({ ...w, id: uid() });
  }
  const wantedKeys = new Set(wanted.map(key));
  next.items = next.items.filter((it) => it.type !== "attachment" || wantedKeys.has(key(it)));
  return next;
}

/* -------------------------------------------------------------------------- */
/*                                 Main component                             */
/* -------------------------------------------------------------------------- */

export default function PrintSection({ packet, data, onChange /*, valueOptions*/ }) {
  const packetId = packet?.id ?? packet?.packet_id ?? null;
  const latest = useMemo(() => latestSectionsByType(packet), [packet]);

  const saved = data?.print || {};
  const defaultItems = useMemo(() => buildDefaultItems(latest), [latest]);

  const [plan, setPlan] = useState(() => ({
    planVersion: 2,
    layout: saved.layout || { pageSize: "Letter", orientation: "portrait", margin: "normal", fontSize: "base" },
    // keep your conservative defaults (no header/footer by default in UI)
    header: saved.header || { showHeader: false, title: packetTitle(packet), showMeta: false, showDate: false },
    footer: saved.footer || {
      showFooter: true,
      showPageNumbers: true,
      disclaimer: "",
      pageNumbers: { enabled: true, withWord: true, size: 15, bold: true, startAfterCover: true },
    },
    items: Array.isArray(saved.items) && saved.items.length ? normalizeItems(saved.items) : defaultItems,
  }));

  // NEW: spinner / disable while generating
  const [isDownloading, setIsDownloading] = useState(false);

  // Keep plan synced to server changes:
  // 1) ensure all static types (incl. "history") are present
  // 2) keep attachments mirrored
  useEffect(() => {
    setPlan((p) => mergeAttachmentsIntoPlan(mergeStaticsIntoPlan(p, latest), latest));
  }, [latest]);

  // Bubble up to parent (PacketBuilder) as you do today
  const first = useRef(true);
  useEffect(() => {
    onChange?.({ print: plan });
    if (first.current) first.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(plan)]);

  // ------------------------------ derived lists ------------------------------
  const included = plan.items.filter((it) => !!it.include);
  const bank = plan.items; // bank shows everything; enabled state reflects existence & include

  // existence resolver (disable if not in packet)
  // Chart pills derive from their parent EU section
  const CHART_DATA_SOURCE = { eu_chart: "eu", eu_income_chart: "eu_income", eu_map: "eu", eu_income_map: "eu_income" };

  // Count active (use=true) comps for chart warnings
  const chartCompCount = useCallback(
    (type) => {
      const srcType = CHART_DATA_SOURCE[type];
      if (!srcType) return 0;
      const sec = latest[srcType];
      const rows = sec?.data?.rows || sec?.data?.equal_uniform?.rows || sec?.data?.equal_uniform_income?.rows || [];
      const cfg = sec?.data?.config || sec?.data?.equal_uniform?.config || sec?.data?.equal_uniform_income?.config || {};
      const subjectId = cfg.subjectId || cfg.subject_id;
      return rows.filter((r) => {
        if (subjectId != null && r.id === subjectId) return false;
        if (!subjectId && rows.indexOf(r) === 0) return false;
        const u = r.use ?? r.include ?? r.selected ?? r.checked;
        return u === true || u === "true" || u === 1 || u === "1" || u === "yes";
      }).length;
    },
    [latest]
  );

  const MAP_TYPES = new Set(["eu_map", "eu_income_map"]);

  const itemExists = useCallback(
    (it) => {
      if (it.type === "attachment") {
        const sec = latest["attachments"];
        const arr = Array.isArray(sec?.data?.attachments) ? sec.data.attachments : [];
        return !!arr.some((a, idx) => (a?.id ?? idx) === it?.options?.attachmentKey && !!a?.url);
      }
      const lookupType = CHART_DATA_SOURCE[it.type] || it.type;
      const sec = latest[lookupType];
      if (!sec) return false;
      // Map pills require map_data to exist in the parent section
      if (MAP_TYPES.has(it.type)) {
        const d = sec?.data || {};
        const md = d.map_data || d.equal_uniform?.map_data || d.equal_uniform_income?.map_data;
        return !!md?.pins?.length;
      }
      return true;
    },
    [latest]
  );

  // ------------------------------ state & helpers ----------------------------
  const [expandedIds, setExpandedIds] = useState(() => new Set()); // expand on first drop
  const expand = (id, v = true) => setExpandedIds((s) => (v ? new Set(s).add(id) : (s.delete(id), new Set(s))));
  const toggleExpand = (id) => setExpandedIds((s) => (s.has(id) ? (s.delete(id), new Set(s)) : new Set(s).add(id)));

  const setItemOption = (id, key, value) =>
    setPlan((p) => ({
      ...p,
      items: p.items.map((it) => (it.id === id ? { ...it, options: { ...it.options, [key]: value } } : it)),
    }));

  const setInclude = (id, v) =>
    setPlan((p) => ({ ...p, items: p.items.map((it) => (it.id === id ? { ...it, include: !!v } : it)) }));

  const moveByArrows = (id, dir) =>
    setPlan((p) => {
      const arr = p.items.slice();
      const onlyIncluded = arr.filter((x) => x.include).map((x) => x.id);
      const currentIncludedIndex = onlyIncluded.indexOf(id);
      if (currentIncludedIndex < 0) return p;

      const targetIncludedIndex = dir === "up" ? currentIncludedIndex - 1 : currentIncludedIndex + 1;
      if (targetIncludedIndex < 0 || targetIncludedIndex >= onlyIncluded.length) return p;

      // translate included indices back into positions in arr
      const fromAbs = arr.findIndex((x) => x.id === onlyIncluded[currentIncludedIndex]);
      const toAbs = arr.findIndex((x) => x.id === onlyIncluded[targetIncludedIndex]);
      const next = arr.slice();
      const [moved] = next.splice(fromAbs, 1);
      next.splice(toAbs, 0, moved);
      return { ...p, items: next };
    });

  /**
   * Insert (or move) an item to a specific slot among *included* items.
   * The algorithm:
   *  - computes the target included slot (0..included.length),
   *  - adjusts for "move down" when the dragged item was already included,
   *  - removes the item, ensures include=true,
   *  - computes absolute insertion index among all items by
   *    looking at the absolute positions of the remaining included items.
   */
  const includeAndMoveToIndex = (id, toIndex /* among included slots 0..N */) =>
    setPlan((p) => {
      const arr = p.items.slice();
      const idx = arr.findIndex((x) => x.id === id);
      if (idx < 0) return p;

      const item = arr[idx];
      const includedIdsBefore = arr.filter((x) => x.include).map((x) => x.id);
      const currIncIdx = includedIdsBefore.indexOf(id);

      // clamp
      let target = Math.max(0, Math.min(toIndex, includedIdsBefore.length));
      // if moving an already-included item "down", the removal shrinks the target by 1
      if (currIncIdx !== -1 && currIncIdx < target) target--;

      // remove item, mark include=true
      arr.splice(idx, 1);
      const moving = { ...item, include: true };

      // absolute positions of remaining included items (in arr without the moving one)
      const absIncludedPos = [];
      for (let j = 0; j < arr.length; j++) {
        if (arr[j]?.include) absIncludedPos.push(j);
      }

      let absInsert;
      if (absIncludedPos.length === 0) {
        absInsert = 0; // first included
      } else if (target <= 0) {
        absInsert = absIncludedPos[0]; // before first included
      } else if (target >= absIncludedPos.length) {
        absInsert = absIncludedPos[absIncludedPos.length - 1] + 1; // after last included
      } else {
        absInsert = absIncludedPos[target]; // before the included at 'target'
      }

      arr.splice(absInsert, 0, moving);
      return { ...p, items: arr };
    });

  // --------------------------- HTML5 Drag and Drop ---------------------------
  const [hoverSlot, setHoverSlot] = useState(null); // integer index among included slots

  const dragStartFromBank = (id) => (e) => {
    e.dataTransfer.setData("text/id", id);
    e.dataTransfer.setData("text/source", "bank");
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };
  const dragStartFromZone = (id) => (e) => {
    e.dataTransfer.setData("text/id", id);
    e.dataTransfer.setData("text/source", "zone");
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };

  const onCanvasDragOver = (e) => {
    e.preventDefault();
    // visual hint: if no explicit slot is hovered, show the trailing slot
    if (included.length > 0 && hoverSlot == null) setHoverSlot(included.length);
  };
  const dropIntoEnd = (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/id");
    if (!id) return;
    includeAndMoveToIndex(id, included.length);
    expand(id, true);
    setHoverSlot(null);
  };

  // slots (between items)
  const dropIntoSlot = (slotIndex) => (e) => {
    e.preventDefault();
    e.stopPropagation(); // don't bubble to canvas
    const id = e.dataTransfer.getData("text/id");
    if (!id) return;
    includeAndMoveToIndex(id, slotIndex);
    expand(id, true);
    setHoverSlot(null);
  };
  const slotDragOver = (slotIndex) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHoverSlot(slotIndex);
  };

  // card-local drop (top half = before, bottom half = after)
  const cardDragOver = (i) => (e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const slot = y < rect.height / 2 ? i : i + 1;
    setHoverSlot(slot);
  };
  const cardDrop = (i) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const id = e.dataTransfer.getData("text/id");
    if (!id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const slot = y < rect.height / 2 ? i : i + 1;
    includeAndMoveToIndex(id, slot);
    expand(id, true);
    setHoverSlot(null);
  };

  // --------------------------- server pdf generation -------------------------
  const downloadServerPdf = async () => {
    if (!packetId || isDownloading) return;
    setIsDownloading(true);
    try {
      const r = await api.post(`/bapb/packets/${packetId}/export/pdf`, { plan }, { responseType: "blob", timeout: 180000 });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `packet_${packetId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "PDF export failed");
    } finally {
      setIsDownloading(false);
    }
  };

  // ------------------------------- bank actions ------------------------------
  const addFromBank = (id) => {
    includeAndMoveToIndex(id, included.length);
    expand(id, true);
  };
  const selectAll = () =>
    setPlan((p) => ({
      ...p,
      items: p.items.map((it) => (itemExists(it) ? { ...it, include: true } : it)),
    }));
  const selectNone = () =>
    setPlan((p) => ({
      ...p,
      items: p.items.map((it) => ({ ...it, include: false })),
    }));

  return (
    <div className="space-y-4">
      {/* BANK */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-base font-semibold text-slate-800 mb-3">Print — Build Your Packet</div>

        <div className="mb-3 flex items-center gap-2">
          <button className="btn" onClick={selectAll}>Include All</button>
          <button className="btn" onClick={selectNone}>Clear All</button>
          {/* Subtle green glow added via .btn-pdf + .glow */}
          <button
            className={`btn btn-pdf ${isDownloading ? "" : "glow"}`}
            onClick={downloadServerPdf}
            disabled={isDownloading}
            title={isDownloading ? "Generating…" : "Generate PDF"}
          >
            {isDownloading ? <span className="spinner" aria-hidden /> : null}
            <span style={{ marginLeft: isDownloading ? 8 : 0 }}>
              {isDownloading ? "Generating…" : "Generate PDF"}
            </span>
          </button>

          {/* spacer */}
          <div style={{ flex: 1 }} />

          {/* Compact, grouped page number controls */}
          <PageNumberControls plan={plan} setPlan={setPlan} />
        </div>

        <div className="mb-3">
          <div className="text-sm text-slate-600 mb-2">Sections Bank (drag or click to add)</div>
          <div className="pill-bank">
            {bank.map((it) => {
              const exists = itemExists(it);
              const added = !!it.include;
              return (
                <button
                  key={it.id}
                  type="button"
                  className={[
                    "pill",
                    added ? "pill-added" : "",
                    !exists ? "pill-disabled" : "",
                  ].join(" ")}
                  title={!exists ? "Not available in this packet" : (added ? "Already added — drag to reorder in canvas" : "Add to packet")}
                  draggable={exists}
                  onDragStart={exists ? dragStartFromBank(it.id) : undefined}
                  onClick={() => exists && addFromBank(it.id)}
                >
                  <span className="pill-label">{it.label}</span>
                  {renderChipForType(it.type)}
                  {!exists && <span className="pill-tag">Not in packet</span>}
                  {added && <span className="pill-tag">Added</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* CANVAS */}
        <div className="canvas" onDragOver={onCanvasDragOver} onDrop={dropIntoEnd}>
          {included.length === 0 ? (
            <div className="canvas-empty">Drag pills here to build your packet</div>
          ) : (
            <div>
              {/* slot before first */}
              <DropSlot index={0} active={hoverSlot === 0} onDragOver={slotDragOver(0)} onDrop={dropIntoSlot(0)} />

              {included.map((it, i) => {
                const exists = itemExists(it);
                const expanded = expandedIds.has(it.id);
                const canUp = i > 0;
                const canDown = i < included.length - 1;

                return (
                  <React.Fragment key={it.id}>
                    <div
                      className={["card", hoverSlot === i ? "card-drop-top" : (hoverSlot === i + 1 ? "card-drop-bottom" : "")].join(" ")}
                      draggable
                      onDragStart={dragStartFromZone(it.id)}
                      onDragOver={cardDragOver(i)}
                      onDrop={cardDrop(i)}
                    >
                      {/* header */}
                      <div className="card-head">
                        <div className="card-title">
                          <button className="chev" onClick={() => toggleExpand(it.id)} title={expanded ? "Collapse" : "Expand"}>
                            {expanded ? "▾" : "▸"}
                          </button>
                          <span className="title-txt">{it.label}</span>
                          {renderChipForType(it.type)}
                          {!exists && <span className="badge-warn">Not in packet</span>}
                        </div>
                        <div className="card-actions">
                          <button className="btn" onClick={() => moveByArrows(it.id, "up")} disabled={!canUp}>↑</button>
                          <button className="btn" onClick={() => moveByArrows(it.id, "down")} disabled={!canDown}>↓</button>
                          <button className="btn" onClick={() => { setInclude(it.id, false); expand(it.id, false); }} title="Remove from packet">Remove</button>
                        </div>
                      </div>

                      {/* chart comp-count warning */}
                      {(it.type === "eu_chart" || it.type === "eu_income_chart") && chartCompCount(it.type) > 10 && (
                        <div className="chart-warn">
                          ⚠ {chartCompCount(it.type)} active comps — chart may look cramped. Consider reducing to 10 or fewer.
                        </div>
                      )}

                      {/* options */}
                      {expanded && (
                        <div className="card-body">
                          <OptionsEditor item={it} setItemOption={setItemOption} />
                        </div>
                      )}
                    </div>

                    {/* slot AFTER this card (i+1) */}
                    <DropSlot
                      index={i + 1}
                      active={hoverSlot === i + 1}
                      onDragOver={slotDragOver(i + 1)}
                      onDrop={dropIntoSlot(i + 1)}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .btn { border: 1px solid #e5e7eb; background: #fff; padding: 6px 10px; border-radius: 10px; font-size: 14px; }
        .btn:hover { border-color: #d1d5db; }
        .chip { font-size: 11px; padding: 2px 6px; border-radius: 999px; border: 1px solid #e5e7eb; background: #f9fafb; }

        .pill-bank {
          display: flex; flex-wrap: wrap; gap: 8px;
          padding: 8px; border: 1px dashed #e5e7eb; border-radius: 12px; background: #fafafa;
        }
        .pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 10px; border-radius: 999px;
          border: 1px solid #e5e7eb; background: white; font-size: 13px;
          cursor: grab; user-select: none;
        }
        .pill:active { cursor: grabbing; }
        .pill .pill-tag {
          font-size: 11px; padding: 1px 6px; border-radius: 999px; border: 1px solid #e5e7eb; background: #f3f4f6; color: #6b7280;
        }
        .pill-added { opacity: 0.75; }
        .pill-disabled { opacity: 0.40; cursor: not-allowed; }

        .canvas {
          border: 1px dashed #e5e7eb; border-radius: 14px; padding: 12px; min-height: 120px; background: #fff;
        }
        .canvas-empty {
          display: flex; align-items: center; justify-content: center;
          color: #64748b; font-size: 14px; height: 96px; background: #f8fafc; border-radius: 10px;
        }

        .drop-slot {
          height: 10px; margin: 8px 0; border-radius: 6px; border: 1px dashed transparent; transition: all .08s ease;
        }
        .drop-slot.active { background: #eff6ff; border-color: #93c5fd; }

        .card {
          border: 1px solid #e5e7eb; background: #fff; border-radius: 12px; margin: 8px 0; padding: 10px; box-shadow: 0 0 0 rgba(0,0,0,0);
          position: relative;
        }
        .card:active { box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06); }
        .card-head { display: flex; align-items: center; gap: 8px; }
        .card-title { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; color: #0f172a; }
        .title-txt { margin-left: 2px; }
        .badge-warn { font-size: 11px; color: #92400e; background: #fffbeb; border: 1px solid #fcd34d; padding: 2px 6px; border-radius: 8px; }
        .chart-warn { font-size: 12px; color: #92400e; background: #fffbeb; border: 1px solid #fcd34d; padding: 6px 10px; border-radius: 8px; margin-top: 6px; }
        .chev { border: 1px solid #e5e7eb; background: #fff; padding: 2px 6px; border-radius: 8px; font-size: 12px; }
        .chev:hover { border-color: #d1d5db; }
        .card-actions { margin-left: auto; display: inline-flex; gap: 6px; }

        /* thin guides when hovering a card (top/bottom) */
        .card.card-drop-top::before,
        .card.card-drop-bottom::after {
          content: "";
          position: absolute; left: 8px; right: 8px; height: 6px; border-radius: 6px;
          background: #eff6ff; border: 1px dashed #93c5fd;
        }
        .card.card-drop-top::before { top: -7px; }
        .card.card-drop-bottom::after { bottom: -7px; }

        /* --- Generate PDF subtle green glow --- */
        .btn-pdf {
          border-color: #86efac;
          color: #065f46;
          box-shadow:
            0 0 0 2px rgba(16,185,129,0.10),
            0 0 10px rgba(16,185,129,0.08);
          transition: box-shadow .2s ease, border-color .2s ease, transform .05s ease;
        }
        .btn-pdf:hover {
          border-color: #34d399;
          box-shadow:
            0 0 0 3px rgba(16,185,129,0.18),
            0 0 14px rgba(16,185,129,0.12);
        }
        .btn-pdf:active { transform: translateY(0.5px); }

        /* gentle breathing glow */
        .btn-pdf.glow { animation: pdfPulse 3.6s ease-in-out infinite; }
        @keyframes pdfPulse {
          0%, 100% {
            box-shadow:
              0 0 0 2px rgba(16,185,129,0.10),
              0 0 10px rgba(16,185,129,0.08);
          }
          50% {
            box-shadow:
              0 0 0 6px rgba(16,185,129,0.08),
              0 0 14px rgba(16,185,129,0.18);
          }
        }
        @media (prefers-reduced-motion: reduce) { .btn-pdf.glow { animation: none; } }

        /* --- Compact grouped page number controls --- */
        .page-num-group {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 9999px;
          font-size: 12px;
        }
        .page-num-group .lbl {
          display: inline-flex; align-items: center; gap: 6px;
          color: #334155;
        }
        .page-num-group .divider {
          width: 1px; height: 18px; background: #e5e7eb; border-radius: 1px;
        }
        .page-num-group input[type="checkbox"] { accent-color: #10b981; }

        .page-num-group .stepper {
          display: inline-flex; align-items: center; gap: 6px;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 2px 6px;
        }
        .page-num-group .stepper .step {
          border: 1px solid #e5e7eb; background: #fff; border-radius: 6px;
          width: 28px; height: 28px; line-height: 1; font-size: 16px;
        }
        .page-num-group .stepper .step:disabled { opacity: .5; }
        .page-num-group .stepper .step-input {
          width: 42px; text-align: center; border: 0; outline: none; background: transparent;
          -moz-appearance: textfield; appearance: textfield;
        }
        .page-num-group .stepper .suffix { color: #64748b; }

        .spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(16,185,129,0.3); border-top-color: rgba(16,185,129,1); border-radius: 50%; animation: spin 0.8s linear infinite; vertical-align: -2px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .btn:disabled { opacity: .65; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

/* ------------------------------ Grouped Page Controls ------------------------------ */

function PageNumberControls({ plan, setPlan }) {
  const enabled =
    !!(plan.footer?.pageNumbers?.enabled || plan.footer?.showPageNumbers);
  const pn = plan.footer?.pageNumbers || {
    enabled: false,
    withWord: true,
    size: 8,
    bold: false,
    startAfterCover: false,
  };

  const setEnabled = (v) =>
    setPlan((p) => ({
      ...p,
      footer: {
        ...p.footer,
        showFooter: true,
        showPageNumbers: v,
        pageNumbers: { ...(p.footer?.pageNumbers || {}), enabled: v },
      },
    }));

  const updatePN = (patch) =>
    setPlan((p) => ({
      ...p,
      footer: {
        ...p.footer,
        pageNumbers: { ...(p.footer?.pageNumbers || {}), ...patch },
      },
    }));

  const clampSize = (n) => Math.max(6, Math.min(24, Number(n) || 8));

  return (
    <div
      className="page-num-group"
      role="group"
      aria-label="Page number controls"
      title="Page number settings for the PDF footer"
    >
      <label className="lbl">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Page numbers</span>
      </label>

      <span className="divider" aria-hidden />

      <label className="lbl" title="Omit number on the cover; start from 1 on the next page">
        <input
          type="checkbox"
          disabled={!enabled}
          checked={!!pn.startAfterCover}
          onChange={(e) => updatePN({ startAfterCover: e.target.checked })}
        />
        <span>Start after cover</span>
      </label>

      <label className="lbl" title='Show the word “Page” before the number'>
        <input
          type="checkbox"
          disabled={!enabled}
          checked={pn.withWord ?? true}
          onChange={(e) => updatePN({ withWord: e.target.checked })}
        />
        <span>Include “Page”</span>
      </label>

      <span className="divider" aria-hidden />

      <div className="lbl stepper" aria-label="Font size">
        <button
          type="button"
          className="step"
          disabled={!enabled}
          onClick={() => updatePN({ size: clampSize((pn.size ?? 8) - 1) })}
          aria-label="Decrease font size"
        >
          –
        </button>
        <input
          className="step-input"
          type="number"
          min={6}
          max={24}
          step={1}
          disabled={!enabled}
          value={String(pn.size ?? 8)}
          onChange={(e) => updatePN({ size: clampSize(e.target.value) })}
        />
        <button
          type="button"
          className="step"
          disabled={!enabled}
          onClick={() => updatePN({ size: clampSize((pn.size ?? 8) + 1) })}
          aria-label="Increase font size"
        >
          +
        </button>
        <span className="suffix">pt</span>
      </div>

      <label className="lbl" title="Bold page numbers">
        <input
          type="checkbox"
          disabled={!enabled}
          checked={!!pn.bold}
          onChange={(e) => updatePN({ bold: e.target.checked })}
        />
        <span>Bold</span>
      </label>
    </div>
  );
}

/* -------------------------------- Options Editor ------------------------------- */

function OptionsEditor({ item, setItemOption }) {
  const { options } = item;

  // Detect "Map" attachments
  const isMapAttachment =
    item.type === "attachment" && /map/i.test(item.label || "");

  // Detect image attachments (using stored MIME or filename)
  const isImageAttachment =
    item.type === "attachment" &&
    (
      (item.options?.mime && item.options.mime.startsWith("image/")) ||
      /\.(png|jpg|jpeg|gif|webp)$/i.test(item.label || "")
    );

  const orientationDefault =
    item.type === "eu" ||
    item.type === "eu_income" ||
    item.type === "history" ||
    item.type === "cost" ||
    item.type === "cost_assessor" ||
    isMapAttachment ||
    isImageAttachment
      ? "landscape"
      : "portrait";

  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <CheckboxRow
        label="Page break before"
        checked={!!options.pageBreakBefore}
        onChange={(v) => setItemOption(item.id, "pageBreakBefore", v)}
      />
      <Select
        label="Orientation"
        value={options.orientation || orientationDefault}
        onChange={(v) => setItemOption(item.id, "orientation", v)}
        options={[
          { value: "portrait", label: "Portrait" },
          { value: "landscape", label: "Landscape" },
        ]}
      />
      <CheckboxRow
        label="Show notes"
        checked={!!options.showNotes}
        onChange={(v) => setItemOption(item.id, "showNotes", v)}
      />
      {item.type === "cost_assessor" && (
        <CheckboxRow
          label="Include Methodology & Defense"
          checked={options.includeMethodology !== false}
          onChange={(v) => setItemOption(item.id, "includeMethodology", v)}
        />
      )}
      <CheckboxRow
        label="Show raw JSON (debug)"
        checked={!!options.showRawJson}
        onChange={(v) => setItemOption(item.id, "showRawJson", v)}
      />
    </div>
  );
}

/* --------------------------------- Drop Slot --------------------------------- */

function DropSlot({ index, active, onDragOver, onDrop }) {
  return (
    <div
      className={`drop-slot ${active ? "active" : ""}`}
      data-index={index}
      onDragOver={onDragOver}
      onDrop={onDrop}
    />
  );
}
